import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ViewportRuler } from '@angular/cdk/scrolling';

import { BugReportService, ClientDiagnosticsService } from '../../../core/services';
import {
  BUG_REPORT_PHOTO_ACCEPTED_TYPES,
  BUG_REPORT_PHOTO_MAX_COUNT,
  BUG_REPORT_PHOTO_MAX_BYTES,
  BUG_REPORT_PHOTO_MAX_MB,
  BugReportClientContext,
  BugReportCreateResponse,
  BugReportCreateValidationError,
} from '../../../core/models';

interface PhotoEntry {
  file: File;
  previewUrl: string;
}

interface FieldErrors {
  description?: string;
  photos?: string;
  general?: string;
}

// One source of truth for the attachment limits — the backend applies the
// same rules to a report's photos and to a thread message's, so the dialog
// and the thread composer must never drift apart.
const MAX_PHOTOS = BUG_REPORT_PHOTO_MAX_COUNT;
const MAX_SIZE_BYTES = BUG_REPORT_PHOTO_MAX_BYTES;
const ACCEPTED_TYPES = BUG_REPORT_PHOTO_ACCEPTED_TYPES;
const ACCEPTED_ATTR = ACCEPTED_TYPES.join(',');
const MAX_SIZE_MB = BUG_REPORT_PHOTO_MAX_MB;

@Component({
  selector: 'app-bug-report-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    TranslateModule,
  ],
  template: `
    <div class="bug-report-dialog">
      <div class="dialog-icon">
        <mat-icon>bug_report</mat-icon>
      </div>

      <h2 class="title">{{ 'BUG_REPORT_DIALOG.TITLE' | translate }}</h2>
      <p class="message">{{ 'BUG_REPORT_DIALOG.SUBTITLE' | translate }}</p>

      <mat-form-field appearance="outline" class="description-field">
        <mat-label>{{ 'BUG_REPORT_DIALOG.DESCRIPTION_LABEL' | translate }}</mat-label>
        <textarea
          matInput
          rows="6"
          [ngModel]="description()"
          (ngModelChange)="description.set($event)"
          [placeholder]="'BUG_REPORT_DIALOG.DESCRIPTION_PLACEHOLDER' | translate"
          [disabled]="submitting()"></textarea>
      </mat-form-field>
      @if (errors().description) {
        <p class="error-text">{{ errors().description }}</p>
      }

      <div class="photos-section">
        <div class="photos-header">
          <span class="photos-label">
            {{ 'BUG_REPORT_DIALOG.ATTACHMENTS_LABEL' | translate }}
            <span class="counter">{{ photos().length }}/{{ MAX_PHOTOS }}</span>
          </span>
          <div class="photo-actions">
            <button
              type="button"
              class="btn-add-photo"
              (click)="takeScreenshot()"
              [disabled]="submitting() || capturingScreenshot() || hasScreenshot() || photos().length >= MAX_PHOTOS">
              @if (capturingScreenshot()) {
                <mat-icon>hourglass_top</mat-icon>
              } @else {
                <mat-icon>screenshot_monitor</mat-icon>
              }
              {{ 'BUG_REPORT_DIALOG.TAKE_SCREENSHOT' | translate }}
            </button>
            <button
              type="button"
              class="btn-add-photo"
              (click)="fileInput.click()"
              [disabled]="submitting() || photos().length >= MAX_PHOTOS">
              <mat-icon>add_photo_alternate</mat-icon>
              {{ 'BUG_REPORT_DIALOG.ADD_PHOTOS' | translate }}
            </button>
          </div>
          <input
            #fileInput
            type="file"
            [accept]="ACCEPTED_ATTR"
            multiple
            hidden
            (change)="onFilesPicked(fileInput)" />
        </div>

        @if (photos().length > 0) {
          <div class="photos-grid">
            @for (photo of photos(); track photo.previewUrl; let i = $index) {
              <div class="photo-thumb">
                <img [src]="photo.previewUrl" [alt]="photo.file.name" />
                <button
                  type="button"
                  class="photo-remove"
                  [attr.aria-label]="'BUG_REPORT_DIALOG.REMOVE_PHOTO' | translate"
                  [title]="'BUG_REPORT_DIALOG.REMOVE_PHOTO' | translate"
                  (click)="removePhoto(i)"
                  [disabled]="submitting()">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
        }

        @if (errors().photos) {
          <p class="error-text">{{ errors().photos }}</p>
        }

        <p class="photos-hint">
          {{ 'BUG_REPORT_DIALOG.ATTACHMENTS_HINT' | translate: { max: MAX_PHOTOS, size: MAX_SIZE_MB } }}
        </p>
      </div>

      @if (submitting()) {
        <mat-progress-bar mode="determinate" [value]="progress()"></mat-progress-bar>
      }

      @if (errors().general) {
        <p class="error-text general">{{ errors().general }}</p>
      }

      <div class="actions">
        <button class="btn-cancel" type="button" (click)="onCancel()" [disabled]="submitting()">
          <mat-icon>close</mat-icon>
          {{ 'COMMON.CANCEL' | translate }}
        </button>
        <button
          class="btn-confirm"
          type="button"
          (click)="onSubmit()"
          [disabled]="!canSubmit()">
          <mat-icon>send</mat-icon>
          {{ 'COMMON.SEND' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .bug-report-dialog {
      padding: 28px 32px;
      min-width: 460px;
      max-width: 560px;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .dialog-icon {
      width: 56px;
      height: 56px;
      margin: 0 auto 16px;
      background: #fef2f2;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;

      mat-icon {
        font-size: 28px;
        width: 28px;
        height: 28px;
        color: #dc2626;
      }
    }

    .title {
      margin: 0 0 6px 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: #3b3b3b;
      text-align: center;
    }

    .message {
      margin: 0 0 18px 0;
      color: #878787;
      font-size: 14px;
      line-height: 1.5;
      text-align: center;
    }

    .description-field {
      width: 100%;

      mat-hint .invalid {
        color: #dc2626;
        font-weight: 600;
      }
    }

    .photos-section {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 12px;
    }

    .photos-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
    }

    .photo-actions {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .photos-label {
      font-size: 13px;
      font-weight: 600;
      color: #5a6a5a;
      display: inline-flex;
      align-items: center;
      gap: 6px;

      .counter {
        font-weight: 500;
        color: #878787;
      }
    }

    .btn-add-photo {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      background: white;
      border: 1.5px dashed #c8d4cb;
      color: #3c7056;
      transition: all 0.2s;

      &:hover:not(:disabled) {
        background: #f3f8f5;
        border-color: #3c7056;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }

    .photos-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
      gap: 8px;
    }

    .photo-thumb {
      position: relative;
      aspect-ratio: 1 / 1;
      border-radius: 10px;
      overflow: hidden;
      border: 1px solid #e5e7eb;
      background: #f8f8f8;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
    }

    .photo-remove {
      position: absolute;
      top: 6px;
      right: 6px;
      width: 22px;
      height: 22px;
      padding: 0;
      border-radius: 50%;
      border: none;
      background: rgba(0, 0, 0, 0.65);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.15s;

      &:hover:not(:disabled) {
        background: rgba(0, 0, 0, 0.85);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      mat-icon {
        font-size: 14px;
        width: 14px;
        height: 14px;
        line-height: 14px;
      }
    }

    .photos-hint {
      margin: 0;
      font-size: 12px;
      color: #9a9a9a;
      line-height: 1.4;
    }

    .error-text {
      margin: 4px 0 0 0;
      font-size: 12px;
      color: #dc2626;
      white-space: pre-line;

      &.general {
        text-align: center;
        margin-top: 12px;
      }
    }

    mat-progress-bar {
      margin: 12px 0 4px;
      border-radius: 4px;
    }

    .actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      margin-top: 16px;
    }

    .actions button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      border: none;
      min-width: 130px;

      &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }

      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }

    .btn-cancel {
      background: white;
      border: 1.5px solid #eaeaea !important;
      color: #5a6a5a;

      &:hover:not(:disabled) {
        background: #f8faf8;
        border-color: #d4ddd4 !important;
      }
    }

    .btn-confirm {
      background: linear-gradient(135deg, #3c7056 0%, #2f5a45 100%);
      color: white;
      box-shadow: 0 4px 12px rgba(60, 112, 86, 0.3);

      &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(60, 112, 86, 0.4);
      }
    }
  `]
})
export class BugReportDialogComponent implements OnDestroy {
  private readonly dialogRef = inject(MatDialogRef<BugReportDialogComponent, BugReportCreateResponse | null>);
  private readonly bugReportService = inject(BugReportService);
  private readonly translate = inject(TranslateService);
  private readonly diagnostics = inject(ClientDiagnosticsService);
  // Recovers the true pre-dialog scroll while the CDK scrollblock is active
  // (window.scrollX/Y read 0 then) — see takeScreenshot().
  private readonly viewportRuler = inject(ViewportRuler);

  // Snapshot diagnostics (last HTTP error, JS error, breadcrumbs, route…)
  // at the moment the user opens the dialog. Freezing here means later
  // requests triggered by filling out the form won't pollute the trail.
  private readonly capturedContext: BugReportClientContext = this.diagnostics.snapshot();

  readonly MAX_PHOTOS = MAX_PHOTOS;
  readonly MAX_SIZE_MB = MAX_SIZE_MB;
  readonly ACCEPTED_ATTR = ACCEPTED_ATTR;

  readonly description = signal('');
  readonly photos = signal<PhotoEntry[]>([]);
  readonly submitting = signal(false);
  readonly progress = signal(0);
  readonly errors = signal<FieldErrors>({});
  readonly capturingScreenshot = signal(false);

  // The page behind the modal can't change (no scroll, no clicks), so a
  // second screenshot would be identical to the first. We detect the
  // screenshot's presence by filename prefix — removing the thumbnail clears
  // this flag automatically and re-enables the button.
  readonly hasScreenshot = computed(() =>
    this.photos().some(p => p.file.name.startsWith('screenshot-'))
  );

  // The form is submittable as long as something is there — text or at
  // least one photo. Backend mirrors this rule (see BugReportService).
  readonly canSubmit = computed(() => {
    if (this.submitting()) return false;
    return this.description().trim().length > 0 || this.photos().length > 0;
  });

  onFilesPicked(input: HTMLInputElement): void {
    const picked = Array.from(input.files ?? []);
    // Reset so re-picking the same file fires (change won't fire otherwise).
    input.value = '';
    if (picked.length === 0) return;
    this.addFiles(picked);
  }

  /**
   * Render the page behind the dialog into a canvas, convert to a PNG file,
   * and feed it through the same `addFiles` pipeline as a manual upload.
   * html2canvas is dynamically imported so the ~70 KB library only loads
   * when the user actually clicks this button (most won't).
   *
   * `ignoreElements` skips the CDK overlay container so the screenshot
   * captures what was UNDER the dialog at click time, not the dialog itself.
   * Bounds are constrained to the visible viewport so we don't accidentally
   * capture a long scrollable page (multi-MB PNG → would breach the 10 MB
   * per-file backend limit).
   */
  async takeScreenshot(): Promise<void> {
    if (this.submitting() || this.capturingScreenshot()) return;
    if (this.photos().length >= MAX_PHOTOS) return;

    this.capturingScreenshot.set(true);
    this.errors.update(e => ({ ...e, photos: undefined }));

    try {
      const html2canvas = (await import('html2canvas')).default;
      const baseOptions = {
        ignoreElements: (el: Element) => {
          const cl = el.classList;
          return cl?.contains('cdk-overlay-container') || cl?.contains('cdk-overlay-backdrop');
        },
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: window.innerWidth,
        height: window.innerHeight,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
      };

      // ROOT CAUSE of the "white screenshot" reports: MatDialog's default CDK
      // BlockScrollStrategy fixes <html> in place while the dialog is open
      // (class `cdk-global-scrollblock` + inline top:-scrollY). Two effects:
      // (i) window.scrollX/Y read 0, and (ii) combined with the app's
      // html{height:100%} (styles.scss), html2canvas treats the fixed root's
      // computed overflow as an element clip one viewport tall sitting ABOVE
      // the crop — so it painted pure background whenever the page had been
      // scrolled. Fix: recover the REAL pre-dialog scroll via ViewportRuler
      // (correct whether or not the scrollblock engaged), crop there, and
      // un-block the CLONED <html> in onclone (the live page is untouched).
      // Trade-off (accepted): position:fixed chrome (topbar) pins to the
      // document top and falls outside the crop when scrolled — the content
      // area is what matters in a bug report.
      const vp = this.viewportRuler.getViewportScrollPosition();
      const captureX = Math.round(vp.left) || 0;
      const captureY = Math.round(vp.top) || 0;
      const scrollBlocked = document.documentElement.classList.contains('cdk-global-scrollblock');

      const started = performance.now();
      const canvas = await html2canvas(document.documentElement, {
        ...baseOptions,
        x: captureX,
        y: captureY,
        scrollX: 0,
        scrollY: 0,
        onclone: (doc: Document) => {
          // Clone-only cleanup: the class carries position:fixed/width/
          // overflow-y (CDK stylesheet); top/left are the only inline styles
          // the CDK sets. With these gone the clone lays out as a normal
          // static document from the top and x/y crop the right region.
          const root = doc.documentElement;
          root.classList.remove('cdk-global-scrollblock');
          root.style.top = '';
          root.style.left = '';
        },
      });

      const blank = this.isCanvasBlank(canvas);
      console.info('[BugReport] capture:',
        `${canvas.width}x${canvas.height}`, 'blank=', blank,
        'liveScroll=', `${window.scrollX},${window.scrollY}`,
        'recoveredScroll=', `${captureX},${captureY}`,
        'scrollBlocked=', scrollBlocked,
        'ms=', Math.round(performance.now() - started));

      // Never attach a blank capture — support kept receiving pure-white PNGs.
      // Surface the failure instead so the user retries or attaches manually.
      if (blank) {
        console.warn('[BugReport] capture came out blank — not attaching');
        this.errors.update(e => ({
          ...e,
          photos: this.translate.instant('BUG_REPORT_DIALOG.ERR_SCREENSHOT_FAILED'),
        }));
        return;
      }

      const blob: Blob | null = await new Promise(resolve =>
        canvas.toBlob(b => resolve(b), 'image/png'),
      );

      if (!blob) {
        // toBlob yields null on a TAINTED canvas (a cross-origin-polluted
        // <canvas> on the page propagates its taint into the capture).
        console.error('[BugReport] canvas.toBlob returned null — capture canvas is likely tainted');
        this.errors.update(e => ({
          ...e,
          photos: this.translate.instant('BUG_REPORT_DIALOG.ERR_SCREENSHOT_FAILED'),
        }));
        return;
      }

      const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
      this.addFiles([file]);
    } catch (err) {
      // Never silent — support debugging depends on knowing WHY a capture
      // failed (html2canvas parse error, tainted canvas, OOM…).
      console.error('[BugReport] screenshot capture failed:', err);
      this.errors.update(e => ({
        ...e,
        photos: this.translate.instant('BUG_REPORT_DIALOG.ERR_SCREENSHOT_FAILED'),
      }));
    } finally {
      this.capturingScreenshot.set(false);
    }
  }

  /**
   * True when a captured canvas is (near-)uniform — the failure signature of
   * the html2canvas scrolled-clone bug (pure background) and of any capture
   * that came out empty. Samples a 48×27 downscale so the check is O(1)
   * regardless of the capture size; tolerant of light noise/antialiasing.
   */
  private isCanvasBlank(canvas: HTMLCanvasElement): boolean {
    if (!canvas.width || !canvas.height) return true;
    try {
      const w = 48;
      const h = 27;
      const probe = document.createElement('canvas');
      probe.width = w;
      probe.height = h;
      const ctx = probe.getContext('2d');
      if (!ctx) return false; // can't tell — let the capture through
      ctx.drawImage(canvas, 0, 0, w, h);
      const data = ctx.getImageData(0, 0, w, h).data;
      const r0 = data[0], g0 = data[1], b0 = data[2];
      let differing = 0;
      for (let i = 4; i < data.length; i += 4) {
        if (Math.abs(data[i] - r0) + Math.abs(data[i + 1] - g0) + Math.abs(data[i + 2] - b0) > 24) {
          differing++;
        }
      }
      return differing / (w * h) < 0.005;
    } catch (err) {
      // getImageData throws SecurityError when the capture inherited a taint
      // from a cross-origin-polluted <canvas> on the page. Can't inspect it —
      // let it through; toBlob will fail loudly if it's truly unusable.
      console.warn('[BugReport] blank-check unavailable (capture canvas tainted?):', err);
      return false;
    }
  }

  removePhoto(index: number): void {
    const current = this.photos();
    const target = current[index];
    if (!target) return;
    URL.revokeObjectURL(target.previewUrl);
    this.photos.set(current.filter((_, i) => i !== index));
    this.errors.update(e => ({ ...e, photos: undefined }));
  }

  onCancel(): void {
    this.releasePreviews();
    this.dialogRef.close(null);
  }

  onSubmit(): void {
    if (!this.canSubmit()) return;
    this.submitting.set(true);
    this.progress.set(0);
    this.errors.set({});

    const description = this.description().trim();
    const files = this.photos().map(p => p.file);

    this.bugReportService.create(description, files, this.capturedContext, pct => this.progress.set(pct))
      .subscribe({
        next: response => {
          this.releasePreviews();
          this.dialogRef.close(response);
        },
        error: (err: HttpErrorResponse) => {
          this.submitting.set(false);
          this.progress.set(0);
          this.errors.set(this.mapHttpError(err));
        },
      });
  }

  ngOnDestroy(): void {
    this.releasePreviews();
  }

  private addFiles(files: File[]): void {
    const current = this.photos();
    const remaining = MAX_PHOTOS - current.length;
    const rejected: string[] = [];
    const accepted: PhotoEntry[] = [];

    if (files.length > remaining) {
      rejected.push(this.translate.instant('BUG_REPORT_DIALOG.ERR_LIMIT_REACHED', {
        max: MAX_PHOTOS,
        ignored: files.length - remaining,
      }));
    }

    for (const file of files.slice(0, remaining)) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        rejected.push(this.translate.instant('BUG_REPORT_DIALOG.ERR_TYPE_UNSUPPORTED', { name: file.name }));
        continue;
      }
      if (file.size > MAX_SIZE_BYTES) {
        rejected.push(this.translate.instant('BUG_REPORT_DIALOG.ERR_TOO_LARGE', { name: file.name, size: MAX_SIZE_MB }));
        continue;
      }
      accepted.push({ file, previewUrl: URL.createObjectURL(file) });
    }

    if (accepted.length > 0) {
      this.photos.set([...current, ...accepted]);
    }
    this.errors.update(e => ({
      ...e,
      photos: rejected.length > 0 ? rejected.join('\n') : undefined,
    }));
  }

  private mapHttpError(err: HttpErrorResponse): FieldErrors {
    const body = err?.error as BugReportCreateValidationError | undefined;
    if (err?.status === 422 && body && body.success === false && body.errors) {
      return { ...body.errors };
    }
    // Support-impersonation session whose agent is no longer valid staff:
    // the backend refuses every write with 403 impersonator_invalid — the
    // report was NOT saved, and retrying won't help until the session ends.
    const code = (err?.error as { code?: string } | undefined)?.code;
    if (err?.status === 403 && code === 'impersonator_invalid') {
      return { general: this.translate.instant('BUG_REPORT_DIALOG.ERR_IMPERSONATOR_INVALID') };
    }
    // The combined upload exceeded the server's limit before any application
    // code ran (PHP post_max_size) — a whole-request cut-off, not a field error.
    if (err?.status === 413 || code === 'payload_too_large') {
      return { photos: this.translate.instant('BUG_REPORT_DIALOG.ERR_PAYLOAD_TOO_LARGE') };
    }
    return { general: this.translate.instant('BUG_REPORT_DIALOG.SUBMIT_ERROR') };
  }

  private releasePreviews(): void {
    for (const p of this.photos()) {
      URL.revokeObjectURL(p.previewUrl);
    }
  }
}
